<script setup>
import { computed, onMounted, ref, defineEmits } from 'vue';
import { useTranslation } from '../composables/useTranslation';

const { t } = useTranslation();

const props = defineProps({
    page: {
        type: Number,
        default: 1,
    },
    perPage: {
        type: Number,
        default: 20,
    },
    total: {
        type: Number,
        default: 0,
    },
    label: {
        type: String,
        default: 'widget',
    },
    all: {
        type: String,
        default: 'All',
    },
    disabled: {
        type: [Number, Boolean],
        default: false,
    }
})

const emit = defineEmits(['update:page', 'update:perPage'])

// Reactive state mirroring props
const currentPage = ref(props.page);
const currentPerPage = ref(props.perPage);

const getDices = computed(() => {
    if (currentPerPage.value === -1) {
        return 1;
    }
    return Math.ceil(props.total / currentPerPage.value);
})

const url = new URL(window.location.href);

const setPage = (page) => {
    if (page < 1 || page > getDices.value) return; // prevent setting invalid page numbers
    currentPage.value = page;
    url.searchParams.set('p', page);
    emit('update:page', page);
    window.history.pushState({}, '', url);
}

onMounted(() => {
    const page = url.searchParams.get('p');
    setPage(page ? Number(page) : 1);
})

const showPerPage = ref(false);

const currentCount = computed(() => {
    // Actual count of items on the current page
    if ( currentPerPage.value === -1) {
        return props.total;
    }
    
    const start = (currentPage.value - 1) * currentPerPage.value;
    const end = Math.min(start + currentPerPage.value, props.total);
    
    // return only the remained items
    return Math.min(props.total, end - start);
})
</script>
<template>
    <div class="flex items-center justify-between flex-col-reverse sm:flex-row gap-3">
        <!-- info  -->
        <div class="flex items-center justify-end gap-1 relative text-slate-600"> {{ t('showing') }} {{ currentCount }} / {{ props.total }} <span class="px-2 text-slate-300">|</span> {{ t('per_page') }} <div
                class="outline-none cursor-pointer font-medium text-indigo-500 lowercase"
                @keydown.prevent="showPerPage = false" tabindex="1" @click.prevent="showPerPage = !showPerPage"
                @blur="showPerPage = false"> {{ currentPerPage === -1 ? total : Math.min(currentPerPage, total) }}
            </div> 
            <transition name="bounce">
                <div v-if="showPerPage"
                    class="absolute z-50 w-28 bottom-full mb-2 bg-white divide-y divide-slate-100 shadow-lg flex flex-col child:cursor-pointer child:px-5 child:py-2 child-hover:bg-indigo-50 child-hover:border-indigo-200 child:transition rounded overflow-hidden">
                    <span v-for="n in [props.all, 500, 100, 50, 20]"
                        @click.prevent="(currentPerPage = Number(n) ? n : -1, showPerPage = false), $emit('update:perPage', n)">{{
                        n }}</span>
                </div>
            </transition>
        </div>
        <!-- pages  -->
        <div
            class="flex items-center justify-start child:cursor-pointer child:px-4 child:h-8 child:flex child:ring-0 child:transition child:items-center rounded overflow-hidden child-hover:opacity-90 child-focus:outline-none">
            <a href="#" @click.prevent="setPage(currentPage - 1)"
                class="text-gray-700 bg-gray-100 border-r border-slate-50" :class="{
                    'opacity-30 pointer-events-none': currentPage < 2 || currentPage >= props.disabled && props.disabled !== false,
                }">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 fill-current" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
            </a>
            <a v-for="dice in getDices" :key="dice" href="javascript:;" class="border-r border-slate-50"
                @click.prevent="setPage(dice)" :class="{
                    'bg-indigo-600 text-white hover:text-white border-indigo-600': dice == currentPage,
                    'text-gray-700 bg-gray-100 hover:text-indigo-500 ': dice != currentPage,
                    'opacity-30 pointer-events-none': props.disabled !== false && dice >= props.disabled,
                }">{{ dice }}</a>
            <a href="#" @click.prevent="setPage(currentPage + 1)" class="text-gray-700 bg-gray-100" :class="{
                'opacity-30 pointer-events-none': currentPage >= getDices || currentPage >= props.disabled && props.disabled !== false,
            }">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 fill-current" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
            </a>
        </div>
    </div>
</template>
